import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";
import { orgService } from "@/services/api";
import { NotificationDrawer } from "@/components/common/NotificationDrawer";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Search, Sun, Moon, LifeBuoy, Menu, ChevronDown, Building2,
  User, Settings, LogOut, PanelLeft, PanelLeftClose,
} from "lucide-react";

export function Header({ title }) {
  const navigate = useNavigate();
  const {
    theme, toggleTheme, setSearchOpen, facilityId, setFacilityId, facilities,
    setMobileNavOpen, collapsed, toggleSidebar,
  } = useUI();
  const { user, logout } = useAuth();
  const [orgName, setOrgName] = useState(null);
  useEffect(() => { orgService.get().then((o) => setOrgName(o.name)).catch(() => {}); }, []);

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border/60 bg-background/70 px-4 backdrop-blur-xl md:px-6"
    >
      <button
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border/70 text-muted-foreground lg:hidden"
        onClick={() => setMobileNavOpen(true)}
        data-testid="mobile-menu-btn"
      >
        <Menu className="h-5 w-5" />
      </button>

      {/* Redundant with the toggle at the bottom of the sidebar itself — this
          one's reachable without having to scroll/look down to the rail's
          bottom, which matters most right after collapsing when the rail is
          only 76px wide. */}
      <button
        className="hidden h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-secondary/60 hover:text-foreground lg:flex"
        onClick={toggleSidebar}
        data-testid="sidebar-toggle-header"
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <PanelLeft className="h-[18px] w-[18px]" /> : <PanelLeftClose className="h-[18px] w-[18px]" />}
      </button>

      <div className="hidden min-w-0 items-center gap-3 md:flex">
        <div className="flex items-center gap-2 rounded-xl border border-border/60 bg-card/50 px-3 py-1.5">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <span className="max-w-[160px] truncate text-sm font-medium">{orgName || "…"}</span>
        </div>
        <Select value={facilityId} onValueChange={setFacilityId}>
          <SelectTrigger className="h-9 w-[168px] border-border/60 bg-card/50 text-sm" data-testid="facility-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Facilities</SelectItem>
            {facilities.map((f) => (
              <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {title && <div className="truncate font-display text-lg font-medium md:hidden">{title}</div>}

      <div className="ml-auto flex items-center gap-1.5">
        <button
          onClick={() => setSearchOpen(true)}
          data-testid="global-search-trigger"
          className="hidden items-center gap-2 rounded-xl border border-border/60 bg-card/50 px-3 py-2 text-sm text-muted-foreground transition hover:border-border hover:text-foreground sm:flex"
        >
          <Search className="h-4 w-4" />
          <span className="hidden lg:inline">Search fleet…</span>
          <kbd className="hidden rounded border border-border/70 bg-secondary px-1.5 py-0.5 font-mono text-[10px] lg:inline">⌘K</kbd>
        </button>
        <button
          onClick={() => setSearchOpen(true)}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-secondary/60 hover:text-foreground sm:hidden"
        >
          <Search className="h-5 w-5" />
        </button>

        <button
          onClick={toggleTheme}
          data-testid="theme-toggle"
          className="flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-secondary/60 hover:text-foreground"
        >
          {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </button>

        <button
          onClick={() => navigate("/help")}
          className="hidden h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-secondary/60 hover:text-foreground sm:flex"
        >
          <LifeBuoy className="h-[18px] w-[18px]" />
        </button>

        <NotificationDrawer />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 flex items-center gap-2 rounded-xl border border-transparent px-1 py-1 transition hover:border-border/60 hover:bg-card/50" data-testid="user-menu-trigger">
              <Avatar className="h-8 w-8 border border-border/60">
                <AvatarFallback className="bg-secondary text-xs font-semibold">{user?.initials || "AR"}</AvatarFallback>
              </Avatar>
              <div className="hidden text-left leading-tight md:block">
                <div className="text-sm font-medium">{user?.name}</div>
                <div className="text-[11px] text-muted-foreground">{user?.role}</div>
              </div>
              <ChevronDown className="hidden h-4 w-4 text-muted-foreground md:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-medium">{user?.name}</div>
              <div className="text-xs font-normal text-muted-foreground">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="mr-2 h-4 w-4" /> Profile</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")}><Settings className="mr-2 h-4 w-4" /> Settings</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} className="text-red-400 focus:text-red-400" data-testid="menu-logout">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
